import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, Label, RadioGroup, RadioGroupItem } from '@/components/ui'
import { ProcuradorUseCase } from '@/features/proposta/brasil/domain/Comprador/use-case/procurador-use-case'
import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import type {
  CompradorVinculoStepSchemaInput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/shared/components/layouts/step-proposta'
import { Cpf } from '@/utils/cpf'

type TSelecaoCompradorMenorVinculo = {
  compradores: TCompradoresCadastrados
  procuradores: TPropostaSchemaInput['cadastroCompradores']['procuradores']
  clientes: TPropostaSchemaInput['cadastroCompradores']['clientes']
  onBack: () => void
  onNext: () => void
}

export const SelecaoProcuradorReutilizar = ({
  compradores,
  procuradores,
  clientes,
  onBack,
  onNext,
}: TSelecaoCompradorMenorVinculo) => {
  const { t } = useTranslation('proposta')
  const { watch, reset } = useFormContext<CompradorVinculoStepSchemaInput, unknown, CompradorVinculoStepSchemaOutput>()
  const procuradorId = watch('dados.id')
  const emailsJaVinculados = watch('emailsJaVinculados')

  const compradoresReutilizaveis = ProcuradorUseCase.extrairClientesReutilizaveisComoProcurador(
    compradores,
    procuradores,
    clientes,
  )

  const handleReutilizarClienteComoProcurador = ProcuradorUseCase.inicializarProcuradorComClienteExistente(
    compradoresReutilizaveis,
    emailsJaVinculados,
    reset,
  )

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{t('cadastro_compradores.steps.selecao_procurador.titulo')}</StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="grid pt-3">
          <RadioGroup
            className="flex flex-col gap-6"
            value={procuradorId}
            onValueChange={handleReutilizarClienteComoProcurador}
          >
            {compradoresReutilizaveis.map((comprador) => (
              <div key={comprador.dados.id} className="flex gap-2">
                <RadioGroupItem value={comprador.dados.id} id={comprador.dados.id} />
                <Label className="font-nunito grid text-base text-neutral-700" htmlFor={comprador.dados.id}>
                  <p className="font-semibold">{comprador.tipo}</p>
                  <p className="font-normal">{comprador.dados.nome}</p>
                  <p className="font-normal text-neutral-600">
                    {t('cadastro_compradores.steps.selecao_procurador.cpf', {
                      cpf: new Cpf(comprador.dados.cpf).format(),
                    })}
                  </p>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12 w-full md:w-auto" variant="outline" onClick={onBack}>
          {t('cadastro_compradores.steps.voltar')}
        </Button>
        <Button className="font-nunito h-12 w-full md:w-27" onClick={onNext}>
          {t('cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
